import clsx from "clsx";
import {
  BriefcaseBusinessIcon,
  CopyIcon,
  EllipsisIcon,
  LoaderIcon,
  UsersIcon,
} from "lucide-react";
import { useNavigate } from "react-router";

import { JobStatusDropdown } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDuplicateJob } from "@/hooks/mutations";
import { useRefetchJobs } from "@/hooks/refetches";
import { useSetJobInJobs } from "@/hooks/sets";
import { Job, JobsResponse } from "@/types";

export const JobsTable = ({ jobs }: { jobs: JobsResponse[] }) => {
  const { mutate: duplicate } = useDuplicateJob();
  const navigate = useNavigate();
  const refetchJobs = useRefetchJobs();
  const setJobInJobs = useSetJobInJobs();

  const isEmptyStateForFilters = jobs.length === 0;

  const handleRowClick = (job: Job) => {
    void navigate(job.id);
  };

  return (
    <div className="flex flex-col overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader
            className={clsx(
              isEmptyStateForFilters && "pointer-events-none opacity-50",
            )}
          >
            <TableRow className="border-none">
              <TableHead className="w-[55%]">
                <div className="flex items-center gap-1">
                  <BriefcaseBusinessIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Job title</p>
                </div>
              </TableHead>
              <TableHead className="w-[19%]">
                <div className="flex items-center gap-1">
                  <UsersIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Candidates</p>
                </div>
              </TableHead>
              <TableHead className="w-[19%]">
                <div className="flex items-center gap-1">
                  <LoaderIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Status</p>
                </div>
              </TableHead>
              <TableHead className="w-[7%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        {isEmptyStateForFilters ? (
          <div className="flex flex-col items-center justify-center gap-6 bg-slate-50 py-28">
            <BriefcaseBusinessIcon className="h-[120px] w-[120px] stroke-[0.7px] text-slate-600" />
            <div className="flex flex-col items-center gap-4">
              <p className="text-3xl font-semibold text-slate-600">
                No jobs found
              </p>
              <p className="text-xl text-slate-500">
                We didn&apos;t find any jobs using the current filters
              </p>
            </div>
          </div>
        ) : (
          <Table>
            <TableBody>
              {jobs.map(({ job, totalCandidates }) => (
                <TableRow
                  className="cursor-pointer"
                  key={job.id}
                  onClick={() => {
                    handleRowClick(job);
                  }}
                >
                  <TableCell className="w-[55%]">
                    <div className="flex flex-col gap-1">
                      <p className="font-medium text-slate-700">{job.title}</p>
                      <p className="text-sm text-slate-500">
                        {job.city}, {job.state} •{" "}
                        {new Date(job.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="w-[19%]">
                    {/* Important to use `<span>` so entire width of cell doesn't get taken. */}
                    <span className="rounded-lg border border-slate-200 p-2">
                      {totalCandidates} total candidate
                      {totalCandidates !== 1 && "s"}
                    </span>
                  </TableCell>
                  <TableCell className="w-[19%]">
                    <JobStatusDropdown job={job} setJob={setJobInJobs} />
                  </TableCell>
                  <TableCell className="w-[7%]">
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <Button size="icon" variant="ghost">
                          <EllipsisIcon className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            // So the table row doesn't get clicked.
                            e.stopPropagation();
                            duplicate(
                              { jobId: job.id },
                              {
                                onSuccess: () => {
                                  refetchJobs();
                                },
                              },
                            );
                          }}
                        >
                          <CopyIcon className="h-4 w-4" /> Duplicate
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
